import { createInstance, i18n, InitOptions, Resource } from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next/initReactI18next';

import { registerI18nFormatters } from './formatters';
import { i18nConfig } from './i18nConfig';

// Common i18n options generator
export const getI18nOptions = (
  i18nInstance: i18n,
  locale: string,
  resources?: Resource
): InitOptions => ({
  lng: locale,
  resources,
  fallbackLng: i18nConfig.defaultLocale,
  supportedLngs: i18nConfig.locales,
  preload: resources ? [] : i18nConfig.locales,
  interpolation: {
    escapeValue: false
  },
  react: {
    useSuspense: false,
    transSupportBasicHtmlNodes: true,
    transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'span']
  }
});

/**
 * Async initialization for server-side rendering.
 * Loads resources dynamically if not provided.
 */
export async function initTranslations(
  locale: string,
  providedInstance?: i18n,
  resources?: Resource
) {
  const i18nInstance = providedInstance || createInstance();

  i18nInstance.use(initReactI18next);

  if (!resources) {
    i18nInstance.use(
      resourcesToBackend(
        (language: string) => import(`../locales/${language}.json`)
      )
    );
  }

  await i18nInstance.init(getI18nOptions(i18nInstance, locale, resources));
  registerI18nFormatters(i18nInstance);

  return {
    i18n: i18nInstance,
    resources: i18nInstance.services.resourceStore.data,
    t: i18nInstance.t
  };
}

/**
 * Sync initialization for client-side hydration.
 * Resources must be pre-loaded (passed from server).
 */
export const initTranslationsSync = (
  locale: string,
  resources: Resource
): i18n => {
  const i18nInstance = createInstance();

  i18nInstance.use(initReactI18next);
  i18nInstance.init(getI18nOptions(i18nInstance, locale, resources));
  registerI18nFormatters(i18nInstance);

  return i18nInstance;
};
